import { format, subDays, startOfMonth } from "date-fns";
import {
  ActivitySeriesBucket,
  AnalyticsBreakdown,
  AnalyticsDimension,
  JourneyEvent,
} from "../api/analyticsAdmin";

export type DateRangePreset = "last7" | "last30" | "thisMonth" | "custom";

export interface DateRange {
  from: string;
  to: string;
}

const fmt = (d: Date): string => format(d, "yyyy-MM-dd");

export const computeDateRange = (
  preset: DateRangePreset,
  today: Date
): DateRange => {
  const to = fmt(today);
  switch (preset) {
    case "last7":
      return { from: fmt(subDays(today, 6)), to };
    case "last30":
      return { from: fmt(subDays(today, 29)), to };
    case "thisMonth":
      return { from: fmt(startOfMonth(today)), to };
    case "custom":
    default:
      return { from: fmt(today), to };
  }
};

export const formatSessionDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const SEGMENT_KEYS: Partial<Record<AnalyticsBreakdown, string[]>> = {
  None: ["all"],
  Platform: ["web", "ios", "android", "native", "unknown"],
  Audience: ["identified", "anonymous"],
};

export const BREAKDOWN_TO_DIMENSION: Partial<
  Record<AnalyticsBreakdown, AnalyticsDimension>
> = {
  Platform: "platform",
  Audience: "audience",
  AgeBand: "age_band",
  Gender: "gender",
  Os: "os",
  RegisteredVia: "registered_via",
};

// A breakdown gets a composition pie when it maps to a by-segment dimension.
// That is every breakdown except "None" (which has a single series).
export const breakdownHasComposition = (b: AnalyticsBreakdown): boolean =>
  b in BREAKDOWN_TO_DIMENSION;

export const deriveSeriesKeys = (series: ActivitySeriesBucket[]): string[] => {
  const totals = new Map<string, number>();
  series.forEach((bucket) =>
    bucket.segments.forEach((segment) => {
      totals.set(segment.key, (totals.get(segment.key) ?? 0) + segment.count);
    })
  );
  return Array.from(totals.entries())
    .sort((a, b) => {
      if (a[0] === "unknown") return 1;
      if (b[0] === "unknown") return -1;
      return b[1] - a[1];
    })
    .map(([key]) => key);
};

// Age bands read most naturally youngest-to-oldest ("18-24", "25-34", …)
// rather than the by-total order deriveSeriesKeys produces. Sort by the leading
// number, keeping "unknown" last.
export const compareAgeBand = (a: string, b: string): number => {
  if (a === "unknown") return 1;
  if (b === "unknown") return -1;
  return parseInt(a, 10) - parseInt(b, 10);
};

// Deterministic segment order for the Search/Clicks funnel breakdowns,
// mirroring the Activity tab's resolveSeriesKeys rules: age bands read
// youngest-to-oldest, everything else by volume descending, "unknown" last.
// `breakdown` is a plain string so this works across AnalyticsBreakdown,
// SearchBreakdown and ClicksBreakdown, which all share the "AgeBand" literal.
export const sortSegments = <T>(
  breakdown: string,
  items: T[],
  getKey: (item: T) => string,
  getVolume: (item: T) => number
): T[] =>
  [...items].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    if (breakdown === "AgeBand") return compareAgeBand(ka, kb);
    if (ka === "unknown") return 1;
    if (kb === "unknown") return -1;
    return getVolume(b) - getVolume(a);
  });

export const resolveSeriesKeys = (
  breakdown: AnalyticsBreakdown,
  series: ActivitySeriesBucket[]
): string[] => {
  const fixed = SEGMENT_KEYS[breakdown];
  if (!fixed) {
    const derived = deriveSeriesKeys(series);
    return breakdown === "AgeBand"
      ? [...derived].sort(compareAgeBand)
      : derived;
  }
  // Drop fixed-legend buckets that carry no data across the whole range, so
  // empty series (e.g. the "native" platform the backend never populates) are
  // not rendered as flat-zero bars. Fall back to the full legend when there is
  // no data at all, keeping a stable legend for empty result sets.
  const totals = new Map<string, number>();
  series.forEach((bucket) =>
    bucket.segments.forEach((segment) => {
      totals.set(segment.key, (totals.get(segment.key) ?? 0) + segment.count);
    })
  );
  const present = fixed.filter((key) => (totals.get(key) ?? 0) > 0);
  return present.length > 0 ? present : fixed;
};

// Breakdowns where the residual "unknown" bucket is really "visitors we could
// not tie to a signed-in user". For those it reads better as "Anonymous"; other
// breakdowns (e.g. Platform) keep the literal "unknown" slug.
const ANONYMOUS_LABEL_BREAKDOWNS = new Set<AnalyticsBreakdown>([
  "Audience",
  "AgeBand",
]);

// Display label for a series/segment key. Only affects presentation; the raw
// key is still used for data lookup, colouring and legend toggling.
export const formatSegmentLabel = (
  breakdown: AnalyticsBreakdown,
  key: string
): string =>
  key === "unknown" && ANONYMOUS_LABEL_BREAKDOWNS.has(breakdown)
    ? "Anonymous"
    : key;

export interface ChartRow {
  bucketStart: string;
  [key: string]: number | string;
}

export const normalizeSeriesToChartData = (
  series: ActivitySeriesBucket[],
  keys: string[]
): ChartRow[] =>
  series.map((bucket) => {
    const row: ChartRow = { bucketStart: bucket.bucketStart };
    keys.forEach((key) => {
      row[key] = 0;
    });
    bucket.segments.forEach((segment) => {
      if (keys.includes(segment.key)) {
        row[segment.key] = segment.count;
      }
    });
    return row;
  });

export interface SessionGroup {
  sessionId: string;
  events: JourneyEvent[];
}

export const groupEventsBySession = (events: JourneyEvent[]): SessionGroup[] => {
  const groups: SessionGroup[] = [];
  events.forEach((event) => {
    const last = groups[groups.length - 1];
    if (last && last.sessionId === event.sessionId) {
      last.events.push(event);
    } else {
      groups.push({ sessionId: event.sessionId, events: [event] });
    }
  });
  return groups;
};

export const parseEventProperties = (
  json: string
): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};
